import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import { getHref } from '../../utils/navigation';
import trackEvent from '../../utils/tracking';
import NoUnderlineLink from '../../atoms/link-no-underline';
import Picture from '../../atoms/picture';
import Button from '../../atoms/button';
import Typography from '../../atoms/typography';
import IconCircleArrow from '../../atoms/icon-circle-arrow';
import Ruler from '../../atoms/ruler';

import { responsiveCSS } from '../../theme/page';

const Wrapper = styled.section`
  ${responsiveCSS}
  padding: 0;

  ${(props) => !props.noWrap && css`
    width: calc(100% + 4em);
    transform: translateX(-2em);
  `}

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    transform: translateX(-6.5vw);
    width: calc(100% + 13vw);
    padding: ${(props) => (props.noWrap ? '0 6.5vw' : '0 6.5vw 0')};
  }

  ${(props) => (props.doodleBg ? css`
    padding-top: 4rem;
    background: white;
    background-image: url(${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette-bg-white-right.jpg), url(${process.env.NEXT_PUBLIC_ASSET_HOST}/images/maisonette-bg-white-left.jpg);
    background-repeat: repeat-y;
    background-position: rop right, top left;
  ` : null)}
`;

const WrapperHeader = styled.header`
  align-items: center;
  display: flex;
  flex-direction: column;
  margin-bottom: ${(props) => props.theme.modularScale['2xlarge']};

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    margin-bottom: ${(props) => props.theme.modularScale.xlarge};
  }
`;

const WrapperTitle = styled(Typography)`
  color: ${(props) => props.theme.color.brand};

  a {
    text-decoration: none;
    font-family: inherit;
  }
`;

const WrapperSubtitle = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
`;

const Cta = styled(Button)`
  border: none;
  border-bottom: .2rem solid ${(props) => props.theme.color.brand};
  color: ${(props) => props.theme.color.brand};
  display: inline-block;
  font-family: ${(props) => props.theme.font.caption};
  font-size: ${(props) => props.theme.modularScale.small};
  padding-bottom: .8rem;
  position: relative;
  text-transform: uppercase;
  line-height: 1rem;
  letter-spacing: .24rem;

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    margin-bottom: ${(props) => props.theme.modularScale.small};
  }
`;

const ProductArrowIcon = styled(IconCircleArrow)`
  height: 2.7rem;
  margin-bottom: ${(props) => props.theme.modularScale.small};
  width: 2.7rem;

  circle,
  path {
    fill: none;
    stroke: ${(props) => props.theme.color.brand};
    stroke-width: 4;
    transform: translate3d(0, 0, 0);
  }
`;

const Items = styled.div`
  max-width: 1400px;
  margin: auto;
  display: flex;
  flex-wrap: wrap;
  align-content: center;
  align-items: start;
  justify-content: center;

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    flex-wrap: ${(props) => (props.noWrap ? 'nowrap' : 'wrap')};
  }
`;

const ItemImage = styled(Picture)`
  margin-bottom: ${(props) => props.theme.modularScale.small};
  width: 100%;

    ${(props) => (props.noWrap ? css`
      img {
        max-height: 160px;
        max-width: 160px;
        object-fit: contain;
      }
    ;` : null)}

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    margin-bottom: 4rem;
  }
`;

const Item = styled.article`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  justify-content: flex-start;
  margin-bottom: ${(props) => props.theme.modularScale['2xlarge']};
  width: ${(props) => (props.noWrap ? '25%' : '50%')};

  &:hover {

    ${ItemImage} {
      ${(props) => (props.wiggleOnHover ? css`
      animation-duration: 0.4s;
      animation-name: ${props.theme.animations.wiggle};
      ` : null)}
    }

    ${ProductArrowIcon} path {
      animation: arrow-head;
        animation-duration: ${(props) => props.theme.animation.slow};
        animation-timing-function: ${(props) => props.theme.animation.easeMove};
        animation-delay: 0s;
        animation-iteration-count: 1;
        animation-direction: normal;
        animation-fill-mode: forwards;
        animation-play-state: running;
    }
  }

  a {
    text-align: center;
    width: ${(props) => (props.noWrap ? '100%' : '90%')};
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    width: 25%;

    a {
      width: ${(props) => (props.noWrap ? '100%' : '80%')};
    }
  }

  @media screen and (min-width: ${(props) => props.theme.breakpoint.medium}) {
    flex-shrink: ${(props) => (props.noWrap ? '1' : '0')};
    width: ${(props) => (props.noWrap ? '130px' : '25%')};
  }
`;

const ItemTitle = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  margin-bottom: ${(props) => props.theme.modularScale.medium};
  line-height: 1.41;
  word-break: break-word;

  ${(props) => props.uppercase && css`
    text-transform: uppercase;
    letter-spacing: 0.24em;
  `}
`;

const ItemSubtitle = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  margin-bottom: ${(props) => props.theme.modularScale.medium};
`;

const TaxonShop = (props) => {
  const items = props?.data?.category_taxon_shop_elements
    ? Object.values(props.data.category_taxon_shop_elements)
    : [];

  const handleTracking = () => trackEvent({
    eventAction: 'Homepage',
    eventCategory: 'Content Engagement',
    eventLabel: `Content Slot 7 (${props?.data?.category_taxon_shop_heading ?? 'Category Taxon Shop'})`
  });

  return (
    <Wrapper
      noWrap={props.data?.category_taxon_shop_one_line}
      doodleBg={
        props.data?.category_taxon_shop_bg_doodles || props?.data?.white_background_with_doodles
      }
      onClick={handleTracking}
    >
      <WrapperHeader>
        {
          props.data.category_taxon_shop_heading
            ? (
              <WrapperTitle element="h1" like="heading-4">
                {props.data.category_taxon_shop_title_url ? (
                  <Link href={props.data.category_taxon_shop_title_url}>
                    <a>
                      {props.data.category_taxon_shop_heading}
                    </a>
                  </Link>
                ) : props.data.category_taxon_shop_heading}
              </WrapperTitle>
            )
            : null
        }
        {
          props.data.category_taxon_shop_subheading
            ? (
              <WrapperSubtitle element="h2" like="paragraph-4">
                {props.data.category_taxon_shop_title_url ? (
                  <Link href={props.data.category_taxon_shop_title_url}>
                    <a>
                      {props.data.category_taxon_shop_subheading}
                    </a>
                  </Link>
                ) : props.data.category_taxon_shop_subheading}
              </WrapperSubtitle>
            )
            : null
        }
        {
          props.data.category_taxon_shop_title_url
            ? (
              <NoUnderlineLink
                href={getHref({ navigation_url: props.data.category_taxon_shop_title_url })}
              >
                <Cta isText inverted>
                  {props.data?.category_taxon_shop_cta ?? 'VIEW ALL'}
                </Cta>
              </NoUnderlineLink>
            )
            : null
        }
      </WrapperHeader>
      <Items noWrap={props.data?.category_taxon_shop_one_line}>
        {
          items.map((item, index) => (
            /* eslint-disable react/no-array-index-key, max-len */
            <Item
              noWrap={props.data?.category_taxon_shop_one_line}
              key={index}
              wiggleOnHover={!props.data.category_taxon_shop_as_taxon}
              paddingsOnMobile={props.data.category_taxon_shop_as_taxon || props.paddingsOnMobile}
            >
              <NoUnderlineLink
                href={getHref({ navigation_url: item.category_taxon_shop_taxon_path })}
                fullWidth
              >
                {
                  item?.category_taxon_shop_element_icon
                  && (
                    <ItemImage
                      noWrap={props.data?.category_taxon_shop_one_line}
                      alt={item.category_taxon_shop_element_icon_alt}
                      {...item.category_taxon_shop_element_icon}
                      circle={!!item?.category_taxon_shop_element_image_type || item?.display_image_as_circle}
                      disableLazyload={props.disableLazyload}
                      size={items.length >= 8 && props.data?.category_taxon_shop_one_line ? 'small' : 'medium'}
                    />
                  )
                }

                {
                  item.category_taxon_shop_row_heading
                    ? (
                      <header>
                        {item?.category_taxon_shop_row_sub_heading
                          && (
                            <ItemSubtitle element="h2" like="dec-1">
                              {item.category_taxon_shop_row_sub_heading}
                            </ItemSubtitle>
                          )}

                        <ItemTitle
                          element={props.data.category_taxon_shop_as_taxon
                            ? 'h1'
                            : 'h6'}
                          like={props.data.category_taxon_shop_as_taxon
                            ? 'heading-5'
                            : 'label-1'}
                          uppercase={
                            props.uppercaseTitles
                            || props.data.category_taxon_shop_one_line
                          }
                        >
                          {item.category_taxon_shop_row_heading}
                        </ItemTitle>
                      </header>
                    )
                    : null
                }
                {
                  // eslint-disable-next-line no-nested-ternary
                  item.category_taxon_shop_taxon_path
                    ? (
                      !props.data?.category_taxon_shop_as_taxon
                        ? <ProductArrowIcon />
                        : (
                          <Cta isText inverted href={item.category_taxon_shop_taxon_path}>
                            {item?.category_taxon_shop_taxon_cta ?? 'SHOP NOW'}
                          </Cta>
                        )
                    )
                    : null
                }
              </NoUnderlineLink>
            </Item>
          ))
        }
      </Items>
      {props.data?.category_taxon_shop_hr && <Ruler />}
    </Wrapper>
  );
};

TaxonShop.defaultProps = {
  uppercaseTitles: false,
  paddingsOnMobile: false,
  disableLazyload: false
};

TaxonShop.propTypes = {
  data: PropTypes.object.isRequired,
  uppercaseTitles: PropTypes.bool,
  paddingsOnMobile: PropTypes.bool,
  disableLazyload: PropTypes.bool
};

TaxonShop.whyDidYouRender = true;

export default TaxonShop;
